import axios, { AxiosError } from "axios";
import { getDefaultCustomer } from "../Store";
import { Address, BauStein } from "../Store/interfaces";

const API_BASE_URL = "https://qas.freyadv.de/eserviceschein/";

// Create an Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Function to set the Authorization header dynamically
const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `${token}`;
    localStorage.setItem("authToken", token); // Store token for persistence
  } else {
    delete api.defaults.headers.common["Authorization"];
    localStorage.removeItem("authToken");
  }
};

// Load token from localStorage when initializing
const storedToken = localStorage.getItem("authToken");
if (storedToken) {
  setAuthToken(storedToken);
}

// ------------------- User Auth -------------------

export const loginUser = async (username: string, usersecret: string) => {
  try {
    const response = await api.post("/login", {
      username: username,
      password: usersecret,
    });

    const { token, bearbeiter, vorname, nachname } = response.data;

    setAuthToken(token);

    const resultArray: [string, string, string, string] = [
      token,
      bearbeiter,
      vorname,
      nachname,
    ];

    return resultArray;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

export const logoutUser = (setToken: (token: undefined) => void) => {
  setAuthToken(null);
  setToken(undefined);
};

export const resetPassword = async (username: string) => {
  try {
    // console.log("fetch reset");
    const response = await api.post(API_BASE_URL + `resetPassword/${username}`);

    if (response.status !== 200 && response.status !== 201) {
      console.error("Fehler beim Zurücksetzen des Passworts: ", response.data);
      return typeof response.data === "string"
        ? response.data
        : JSON.stringify(response.data);
    } else {
      console.log("allet schier");
    }
    return; // Kein Fehler
  } catch (error) {
    console.error("An Error occurred", error);

    if (axios.isAxiosError(error)) {
      // Behandle den Fehler als AxiosError
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        // Sicherstellen, dass die Antwortdaten als string zurückgegeben werden
        const errorMessage = axiosError.response.data;
        return typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage);
      } else {
        return "Ein Netzwerkfehler ist aufgetreten.";
      }
    } else {
      // Für nicht-Axios Fehler
      return "Ein unbekannter Fehler ist aufgetreten.";
    }
  }
};

// ------------------- Bausteine -------------------

export const getBausteine = async () => {
  try {
    const response = await api.get(API_BASE_URL + "bausteine");
    const { data } = response;

    if (response.status === 200) {
      return data;
    }
  } catch (error) {
    alert(
      "Fehler beim Laden der Textbausteine. Bitte versuche es später erneut!"
    );
  }
};

export const postBaustein = async (data: BauStein) => {
  try {
    const response = await api.post(API_BASE_URL + `bausteine`, {
      titel: data.titel,
      inhalt: data.inhalt,
    });
    return response.data;
  } catch (error) {
    alert(
      "Fehler beim Speichern des Textbausteins. Bitte versuche es später erneut."
    );
  }
};

export const deleteBaustein = async (id: string) => {
  try {
    const response = await api.delete(API_BASE_URL + `bausteine/${id}`);
    if (response.status === 200) {
      return;
    }
  } catch (error) {
    alert(
      "Fehler beim Löschen des Textbausteins. Bitte versuche es später erneut."
    );
  }
};

export const updateBaustein = async (id: string, modifiedData: BauStein) => {
  try {
    const response = await api.post(API_BASE_URL + `bausteine/${id}`, {
      titel: modifiedData.titel,
      inhalt: modifiedData.inhalt,
    });
    if (response.status === 201) {
      return;
    }
  } catch (error) {
    alert(
      "Fehler beim Updaten des Textbausteins. Bitte versuche es später erneut."
    );
  }
};

// ------------------- Kundensuche -------------------

export const getCustomer = async (search: string) => {
  try {
    const response = await api.get(API_BASE_URL + `kunden/search/${search}`);
    const { data } = response;

    if (response.status === 200) {
      return data;
    } else {
      return getDefaultCustomer();
    }
  } catch (error) {
    alert(
      "Fehler beim Laden der Textbausteine. Bitte versuche es später erneut!"
    );
  }
};

// ------------------- Anfahrtsberechnung -------------------

export const getLatLongFromAddress = async (address: string) => {
  const graphhopperURL = `https://graphhopper.com/api/1/geocode?key=693ccc0e-b0cb-4daf-814b-fcb0b4f07f7f&q=${address}`;

  try {
    const response = await axios.get(graphhopperURL, {
      headers: {
        "Content-Type": "application/json",
        Accept: "*",
      },
    });

    if (response.status === 200) {
      return response.data.hits[0].point;
    }
  } catch (error: any) {
    console.log("Error in getLatLongFromAddress: " + error.errorMessage);
    return 0;
  }
};

export const getCalculatedDistance = async (
  lat1: number,
  lat2: number,
  long1: number,
  long2: number
) => {
  const query = "693ccc0e-b0cb-4daf-814b-fcb0b4f07f7f";
  const url = `https://graphhopper.com/api/1/route?key=${query}`;

  const body = JSON.stringify({
    points: [
      [long1, lat1],
      [long2, lat2],
    ],
    snap_preventions: ["motorway", "tunnel"],
    details: ["road_class", "surface"],
    profile: "car",
    locale: "de",
    instructions: false,
    calc_points: false,
    points_encoded: false,
  });

  try {
    const response = await axios.post(url, body, {
      headers: {
        "Content-Type": "application/json",
        Accept: "*",
      },
    });

    if (response.status === 200) {
      return response.data.paths[0];
    } else return 0;
  } catch (error: any) {
    console.log("Error in getCalculatedDistance: " + error.errorMessage);
  }
};

// ------------------- Materialliste -------------------

export const getMaterialList = async (all: boolean) => {
  try {
    const url_addition = all ? "materialien" : "materialien?view=selected";
    const response = await api.get(API_BASE_URL + url_addition);

    if (response.status === 200) {
      return response.data;
    } else return null;
  } catch (error: any) {
    console.log("Error in getMaterialList: " + error.errorMessage);
  }
};

// ------------------- Materialliste -------------------

export const postServiceschein = async (body: any) => {
  try {
    const response = await api.post(API_BASE_URL, body);

    console.log("body", body);
    console.log("response", response);

    if (response.status === 201) {
      return response.data;
    } else {
      throw new Error(
        `Fehler beim Senden. Server antwortete mit: ${response.status}`
      );
    }
  } catch (error: any) {
    console.log("Error in postServiceschein: " + error.errorMessage);
    return null;
  }
};

// ------------------- Adressenliste -------------------

export const getStoredAddresses = async () => {
  try {
    const response = await api.get(API_BASE_URL + "adressen");

    console.log(response.data);

    return response.data;
  } catch (error: any) {
    console.log("Error in getStoredAddresses: " + error.errorMessage);
  }
};

export const postAdresse = async (data: string) => {
  try {
    const body = {
      adresse: data,
      is_default: false,
    };

    console.log(body);

    const response = await api.post(API_BASE_URL + "adressen", body);

    if (response.status === 201) {
      return "erfolgreich";
    } else {
      return `Fehler beim Upload ${response.status}: ${response.statusText}`;
    }
  } catch (error: any) {
    console.log("Error in postAdresse: " + error.errorMessage);
  }
};

export const updateAdresse = async (data: Address) => {
  try {
    const body = {
      adresse: data.adresse,
      is_default: data.is_default,
    };

    const response = await api.post(API_BASE_URL + `adressen/${data.id}`, body);

    if (response.status === 201) {
      return "erfolgreich";
    } else {
      return `Fehler beim Upload ${response.status}: ${response.statusText}`;
    }
  } catch (error: any) {
    console.log("Error in updateAdresse: " + error.errorMessage);
  }
};

export const deleteStoredAddress = async (id: number) => {
  try {
    const response = await api.delete(API_BASE_URL + `adressen/${id}`);

    if (response.status === 200) {
      return;
    }
  } catch (error: any) {
    console.log("Error in deleteStoredAddress: " + error.errorMessage);
  }
};

export default api;
